import type { MouseEventHandler } from 'react';
import { Show, ShowFirstMatching } from '@components/Show';
import { IdentityProvider } from '@domain/schemas/identity-provider';
import type { Team } from '@domain/schemas/team.schema';
import type { UserRole } from '@domain/schemas/user-role';
import type { CanonicalUserRecordWithCapabilities } from '@root/helpers/user-management/types';
import { UserLifeCycleStatus } from '@root/helpers/user-management/types';
import language from '@src/language/language';
import type { CellContext } from '@tanstack/react-table';
import { createColumnHelper } from '@tanstack/react-table';

import { createdAtAccessor, teamsAccessor, updatedAtAccessor } from './shared';

const columnHelper = createColumnHelper<CanonicalUserRecordWithCapabilities>();

const pillBaseClasses = 'inline-flex items-center gap-x-1.5 rounded-xl px-3 py-1 text-xs font-semibold';

const USER_LIFE_CYCLE_STATUSES = [
  {
    value: UserLifeCycleStatus.Active,
    label: 'Active',
    labelClassName: `${pillBaseClasses} bg-green-100 text-green-800`,
    predicate: (user: CanonicalUserRecordWithCapabilities) =>
      user.capabilitiesManager.userLifeCycleStatus === UserLifeCycleStatus.Active,
  },
  {
    value: UserLifeCycleStatus.Pending,
    label: 'Pending',
    labelClassName: `${pillBaseClasses} bg-yellow-100 text-yellow-800`,
    predicate: (user: CanonicalUserRecordWithCapabilities) =>
      user.capabilitiesManager.userLifeCycleStatus === UserLifeCycleStatus.Pending,
  },
  {
    value: UserLifeCycleStatus.Disabled,
    label: 'Disabled',
    labelClassName: `${pillBaseClasses} bg-red-100 text-red-800`,
    predicate: (user: CanonicalUserRecordWithCapabilities) =>
      user.capabilitiesManager.userLifeCycleStatus === UserLifeCycleStatus.Disabled,
  },
  {
    value: UserLifeCycleStatus.ResetRequired,
    label: 'Reset required',
    labelClassName: `${pillBaseClasses} bg-yellow-100 text-yellow-800`,
    predicate: (user: CanonicalUserRecordWithCapabilities) =>
      user.capabilitiesManager.userLifeCycleStatus === UserLifeCycleStatus.ResetRequired,
  },
];

const getStatusLabel = (user: CanonicalUserRecordWithCapabilities) =>
  USER_LIFE_CYCLE_STATUSES.find(({ predicate }) => predicate(user))?.label ??
  user.capabilitiesManager.userLifeCycleStatus ??
  '';

const Status = ({ user }: { user: CanonicalUserRecordWithCapabilities }) => {
  const statusConfig = USER_LIFE_CYCLE_STATUSES.find(({ predicate }) => predicate(user));

  if (!statusConfig) {
    return <span className={`${pillBaseClasses} bg-gray-100 text-gray-800`}>Unknown</span>;
  }

  return <span className={statusConfig.labelClassName}>{statusConfig.label}</span>;
};

const identityProviderOptions = [
  { value: IdentityProvider.Internal, label: 'Username & Password' },
  { value: IdentityProvider.External, label: 'Single Sign On' },
] as const;

const identityProviderOptionsMapping = identityProviderOptions.map((item) => ({
  value: item.value,
  label: item.label,
}));

export const getUsersTableColumns = ({
  handleEditUserClick,
  handleResendButtonClick,
  teams,
}: {
  handleEditUserClick: (user: CanonicalUserRecordWithCapabilities) => MouseEventHandler<HTMLAnchorElement> | undefined;
  handleResendButtonClick: (
    user: CanonicalUserRecordWithCapabilities,
  ) => MouseEventHandler<HTMLAnchorElement> | undefined;
  teams: Team[];
}) => [
  columnHelper.accessor('id', {
    id: 'id',
    enableColumnFilter: false,
    enableHiding: false,
  }),
  columnHelper.accessor('firstName', {
    id: 'firstName',
    header: 'First Name',
    enableColumnFilter: false,
    sortingFn: 'text',
    sortUndefined: -1,
    filterFn: 'arrIncludesSome',
    meta: {
      tooltip: true,
    },
  }),
  columnHelper.accessor('lastName', {
    id: 'lastName',
    header: 'Last Name',
    enableColumnFilter: false,
    sortingFn: 'text',
    sortUndefined: -1,
    filterFn: 'arrIncludesSome',
    meta: {
      tooltip: true,
    },
  }),
  columnHelper.accessor((user) => (user.role ? language.user.role[user.role as UserRole] : user.role), {
    id: 'role',
    header: 'Role',
    size: 120,
    filterFn: (row, _, filteredRoleLabels) =>
      filteredRoleLabels?.includes(language.user.role[row.original.role as UserRole]),
    meta: {
      filter: {
        placeholder: 'All roles',
      },
      tooltip: true,
    },
  }),
  teamsAccessor<CanonicalUserRecordWithCapabilities>(columnHelper, teams),
  columnHelper.accessor('email', {
    id: 'email',
    header: 'Email',
    filterFn: 'arrIncludesSome',
    enableHiding: false,
    size: 200,
    meta: {
      filter: {
        placeholder: 'All emails',
      },
      tooltip: true,
    },
  }),
  columnHelper.accessor((user) => getStatusLabel(user), {
    id: 'status',
    header: 'Status',
    filterFn: 'arrIncludesSome',
    size: 122,
    cell: (cellContext: CellContext<CanonicalUserRecordWithCapabilities, string>) => (
      <Status user={cellContext.row.original} />
    ),
    meta: {
      filter: {
        placeholder: 'All statuses',
      },
      dataCellClassName: 'whitespace-nowrap py-4 pl-0 align-top',
      headingLinkClassName: 'pl-0!',
    },
  }),
  createdAtAccessor(columnHelper),
  updatedAtAccessor(columnHelper),
  columnHelper.accessor('identityProvider', {
    id: 'identityProvider',
    header: 'Method',
    filterFn: 'arrIncludesSome',
    minSize: 184,
    cell: (cellContext) => {
      const identityProvider = cellContext.row.original.identityProvider;

      if (!identityProvider) {
        return 'Unknown';
      }

      return identityProvider === IdentityProvider.Internal ? 'Username & Password' : 'Single Sign On';
    },
    meta: {
      filter: {
        placeholder: 'All methods',
        optionsMapping: identityProviderOptionsMapping,
      },
    },
  }),
  columnHelper.display({
    id: 'edit',
    enableColumnFilter: false,
    enableHiding: false,
    size: 70,
    minSize: 70,
    maxSize: 70,
    cell: (cellContext) => (
      <ShowFirstMatching>
        <Show when={cellContext.row.original.capabilitiesManager.canBeUpdatedAsPended}>
          <a
            href="#"
            onClick={handleResendButtonClick(cellContext.row.original)}
            className="text-primary-600 hover:text-primary-900"
          >
            Update
          </a>
        </Show>
        <Show when={cellContext.row.original.capabilitiesManager.canBeEdited}>
          <a
            href="#"
            onClick={handleEditUserClick(cellContext.row.original)}
            className={'text-primary-600 hover:text-primary-900'}
          >
            Edit
          </a>
        </Show>
      </ShowFirstMatching>
    ),
    meta: {
      dataCellClassName: 'relative truncate whitespace-nowrap px-1 py-0 text-center text-sm font-medium',
    },
  }),
];
